import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Input,
    Flex,
    Text,
    Avatar,
    useColorModeValue,
    IconButton,
    VStack,
    useToast,
    Link,
    Tooltip
} from '@chakra-ui/react';
import { IoChatbubbleEllipses } from 'react-icons/io5';
import { IoMdCloseCircle } from "react-icons/io";
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import Dremotech_ChatBotAI from '../../assets/Dremotech_ChatBotAI.webp';
import { FaWhatsapp } from 'react-icons/fa';
import LightLogo from '../../assets/MainLogo.webp';
import MainLogoOnlineStatus from '../../assets/MainLogoOnlineStatus.webp';

const MotionBox = motion(Box);

const Chatbot = ({ onOpenChange }) => {
    // Helper function to format timestamp as "HH:MM AM/PM"
    const formatTimestamp = (date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content:
                'Hello! Welcome to Dremotech. How can I assist you with our services today?',
            timestamp: formatTimestamp(new Date()),
        },
    ]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false); // Added missing state
    const [isTyping, setIsTyping] = useState(false);
    const [typingText, setTypingText] = useState('');
    const messagesEndRef = useRef(null);
    const toast = useToast();
    const [showPopup, setShowPopup] = useState(false);

    // Company context for DremoTech
    const COMPANY_CONTEXT = `
📖 About Dremotech Solutions

Dremotech Solutions is a forward-thinking software development, digital services, and AI solutions company, committed to providing affordable, high-performance IT services tailored to the unique needs of businesses and startups. Founded with a mission to make premium technology solutions accessible to organizations of all sizes, we specialize in transforming innovative ideas into scalable, interactive, and results-driven digital products.
📊 our Services
🖥️ Website Development

    Custom-designed business and personal websites

    E-Commerce solutions with integrated payment gateways

    Responsive, SEO-optimized designs

    CMS integration (WordPress, custom admin panels)

📱 Mobile App Development

    Cross-platform and native apps

    Android & iOS solutions

    E-Commerce, booking apps, and custom business tools

📈 SEO & Digital Marketing

    Complete on-page and off-page SEO

    Google Ads campaign management

    Social Media Marketing on platforms like Facebook & Instagram

    Conversion-optimized digital advertising

🎨 Graphic Design & Branding

    Logo design and visual brand identity

    Marketing materials: brochures, business cards, social creatives

    Social media post designs and banners

🌐 AR/VR Development

    Augmented and Virtual Reality experiences for training, real estate, education, and marketing

    3D product showcase solutions

    Immersive virtual tours and simulations

🌐 AI Solutions

    AI-powered chatbots and virtual assistants

    smart analytics dashboards

    Custom AI model development for specific business needs

    personalized recommendation systems

    intelligent business tools tailored to optimize operations and improve decision-making
    

📬 Contact Details

    📞 Phone: +91 9747284683

    📧 Email: dremotechsolutions@gmail.com

    🌐 Website: www.dremotech.com

📌 Why Choose Dremotech Solutions?

✅ Budget-friendly, professional digital solutions
✅ Cutting-edge technologies
✅ Dedicated, responsive customer support
✅ Proven track record in multiple domains
✅ Custom, scalable solutions tailored to client goals
`;

    // Auto-scroll to the latest message
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
        onOpenChange?.(isOpen);
    }, [messages, isOpen, onOpenChange]);

    // Added useEffect for 2-Second Delay:
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!isOpen) {
                setShowPopup(true);
            }
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    // Added Auto-Dismiss Logic: 
    useEffect(() => {
        if (showPopup) {
            const autoDismiss = setTimeout(() => {
                setShowPopup(false);
            }, 10000);
            return () => clearTimeout(autoDismiss);
        }
    }, [showPopup]);

    // Dismiss Popup on Chat Open: 
    useEffect(() => {
        if (isOpen) {
            setShowPopup(false);
        }
    }, [isOpen]);

    // Function to check for contact-related keywords
    const hasContactKeywords = (message) => {
        const keywords = [
            'price',
            'cost',
            'estimate',
            'amount',
            'contact',
            'phone',
            'email',
            'quote',
            'pricing',
            'how much',
            'budget',
            'rate',
            'discuss'
        ];
        const regex = new RegExp(keywords.join('|'), 'i');
        return regex.test(message);
    };

    // Handle sending a message
    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;

        const userMessage = { role: 'user', content: newMessage, timestamp: formatTimestamp(new Date()), };
        let updatedMessages = [...messages, userMessage];

        // Check for contact-related keywords
        if (hasContactKeywords(newMessage)) {
            const contactMessage = {
                role: 'assistant',
                type: 'contact',
                content: 'For more details, reach out to us directly!',
                timestamp: formatTimestamp(new Date()),
            };
            updatedMessages = [...updatedMessages, contactMessage];
        }
        setMessages(updatedMessages);
        setNewMessage('');
        setIsLoading(true);
        setIsTyping(true);
        setTypingText('');

        try {
            const startTime = Date.now();
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [
                            {
                                parts: [
                                    {
                                        text: `You are a friendly, knowledgeable, and professional assistant for Dremotech Solutions — a dynamic technology company specializing in custom Website Development, Mobile App Development, Graphic Design & Branding, Digital Marketing, SEO, AR/VR Solutions, and AI-powered services. Use the following context to accurately answer questions or provide assistance based on Dremotech's service offerings: ${COMPANY_CONTEXT}\n

If the user greets you (e.g., "Hi," "Good morning," "Hello"), respond warmly wit…ervices. For example, reply with something like: "Good morning! How can I assist you with Dremotech’s website development, mobile apps, branding, digital marketing, AR/VR, or AI solutions today?"\n

Always maintain a courteous, professional tone and tailor your responses to align with Dremotech Solutions' expertise and services. Do not disclose the technology stack or specific tools (e.g., programming languages, frameworks, or platforms) used by Dremotech Solutions.\n

User input: ${newMessage}`,
                                    },
                                ],
                            },
                        ],
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}: ${await response.text()}`);
            }

            const data = await response.json();
            console.log('API Response:', data);
            if (!data.candidates || !data.candidates[0]?.content?.parts?.[0]?.text) {
                throw new Error('Invalid API response structure');
            }
            const reply = data.candidates[0].content.parts[0].text;

            const apiTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 3000 - apiTime);
            setIsTyping(false);
            typeText(reply, remainingTime);
        } catch (error) {
            console.error('Error details:', error.message, error.stack);
            setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: 'Sorry, something went wrong. Please try again.', timestamp: formatTimestamp(new Date()), },
            ]);
            setIsTyping(false);
        } finally {
            setIsLoading(false);
        }
    };

    // Typing animation with dynamic speed
    const typeText = (text, maxDuration) => {
        const charCount = text.length;

        if (charCount < 50) {
            setMessages((prev) => [...prev, { role: 'assistant', content: text, timestamp: formatTimestamp(new Date()), }]);
            setTypingText('');
            setIsLoading(false);
            return;
        }

        const minSpeed = 10;
        const typingSpeed = Math.max(minSpeed, maxDuration / charCount);

        let index = 0;
        let currentText = '';

        const animate = () => {
            if (index < text.length) {
                currentText += text[index];
                setTypingText(currentText);
                index++;
                scrollToBottom();
                setTimeout(animate, typingSpeed);
            } else {
                setMessages((prev) => [...prev, { role: 'assistant', content: text, timestamp: formatTimestamp(new Date()), }]);
                setTypingText('');
                setIsLoading(false);
            }
        };

        animate();
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSendMessage();
    };

    // Toggle chatbox visibility
    const toggleChatbox = () => {
        setIsOpen(!isOpen);
    };

    // Updated color scheme
    const bgColor = useColorModeValue('#f5f0e6', '#1A202C');
    const messageBgUser = useColorModeValue('#2563EB', '#1E40AF');
    const messageBgAssistant = useColorModeValue('#DBEAFE', '#1E3A8A');
    const textColor = useColorModeValue('gray.400', 'gray.300');
    const messageTextColorUser = useColorModeValue('#FFFFFF', '#FFFFFF');
    const messageTextColorAssistant = useColorModeValue('#1E40AF', '#FFFFFF');
    const inputBg = useColorModeValue('#FFFFFF', '#374151');
    const inputTextColor = useColorModeValue('#1F2937', '#F3F4F6');
    const placeholderColor = useColorModeValue('#9CA3AF', '#9CA3AF');
    const typingColor = useColorModeValue('#6B7280', '#D1D5DB');
    const buttonBg = useColorModeValue('#2563EB', '#3B82F6');
    const linkColor = useColorModeValue('#2563EB', '#60A5FA');
    const timestampColor = useColorModeValue('#6B7280', '#c8cfdb');
    const contactBg = useColorModeValue('#EDF2F7', '#2D3748');
    const popupBg = useColorModeValue('#FFFFFF', '#2D3748'); // Popup background color

    const inputBorderColor = useColorModeValue('#E5E7EB', '#4B5563');
    const inputHoverBorderColor = useColorModeValue('#D1D5DB', '#6B7280');
    const buttonHoverBg = useColorModeValue('#1E40AF', '#2563EB');

    const markdownComponents = {
        p: ({ children }) => <Text fontSize="sm">{children}</Text>,
        strong: ({ children }) => <Text as="strong" fontWeight="bold">{children}</Text>,
        a: ({ href, children }) => (
            <Text as="a" color={linkColor} href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </Text>
        ),
        ul: ({ children }) => <Box as="ul" pl={4}>{children}</Box>,
        li: ({ children }) => <Box as="li" fontSize="sm">{children}</Box>,
    };

    return (
        <>
            {/* Floating Chat Icon with Text */}
            <VStack
                position="fixed"
                bottom={{ md: "2px", base: "-15px" }}
                right={{ md: "20px", base: "8px" }}
                spacing={2}
                align="center"
                zIndex={1200}
            >
                <IconButton
                    aria-label="Toggle chatbot"
                    bgImg={Dremotech_ChatBotAI}
                    bgSize="cover"
                    borderRadius="full"
                    colorScheme="transparent"
                    size="lg"
                    boxShadow="lg"
                    onClick={toggleChatbox}
                    transition="box-shadow 0.2s ease"
                    _hover={{
                        boxShadow: '0 0 8px 2px rgba(37, 99, 235, 0.6)',
                    }}
                />
                <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color={textColor}
                    opacity={0}
                    transition="opacity 0.2s ease"
                    _hover={{ opacity: 1 }}
                    sx={{ '.chakra-stack:hover &': { opacity: 1 } }}
                >
                    Dremotech AI
                </Text>
                <AnimatePresence>
                    {showPopup && (
                        <MotionBox
                            position="absolute"
                            bottom={{ md: "95px", base: "90px" }}
                            right={{ md: "20px", base: "20px" }}
                            bg={popupBg}
                            borderRadius="md"
                            p={2}
                            px={3}
                            boxShadow="md"
                            width={{md:"200px",base:"200px"}}
                            zIndex={1250}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                            className="animate__animated animate__bounce animate__infinite animate__slow"
                        >
                            <Text fontSize="sm" color={textColor}>
                                Hi 👋, how can I help you?
                            </Text>
                            <Box
                                position="absolute"
                                bottom="-5px"
                                right={{ md: "10px", base: "5px" }}
                                width="0"
                                height="0"
                                borderLeft="5px solid transparent"
                                borderRight="5px solid transparent"
                                borderTop={`5px solid ${popupBg}`}
                            />
                        </MotionBox>
                    )}
                </AnimatePresence>
            </VStack>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <MotionBox
                        position="fixed"
                        bottom="85px"
                        right="20px"
                        w="300px"
                        h="430px"
                        bg={bgColor}
                        borderRadius="xl"
                        boxShadow="2xl"
                        display="flex"
                        flexDirection="column"
                        p={4}
                        zIndex={1100}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Header */}
                        <Flex justify="space-between" align="center" mb={3} borderBottom="1px solid" borderColor='#E2E8F0'>
                            <Avatar size="sm" src={MainLogoOnlineStatus} mt={-2} />
                            <Flex direction="column" m={0} p={0} mr={5}>
                                <Text fontSize="lg" fontWeight="bold" color={messageTextColorAssistant} mr={3}>
                                    Dremotech AI
                                </Text>
                                <Text fontSize="xs" color={textColor} mt={-4}>
                                    Online
                                </Text>
                            </Flex>
                            <Tooltip label="Close Chat">
                                <IconButton
                                    aria-label="Close chatbot"
                                    icon={<IoMdCloseCircle />}
                                    colorScheme="transparent"
                                    size="lg"
                                    color="red.600"
                                    onClick={toggleChatbox}
                                    _hover={{ color: 'red.700' }}
                                    transition="color 0.2s ease"
                                    mt={-5}
                                />
                            </Tooltip>
                        </Flex>

                        {/* Message Area */}
                        <Box flex="1" overflowY="auto" mb={2}>
                            {messages.map((msg, index) => (
                                <Flex
                                    key={index}
                                    justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                                    mb={2}
                                >
                                    {msg.type === 'contact' ? (
                                        <Box
                                            maxW="75%"
                                            bg={contactBg}
                                            borderRadius="lg"
                                            p={3}
                                            color={messageTextColorAssistant}
                                            boxShadow={'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'}
                                        >
                                            <Flex align="center" mb={1}>
                                                <Avatar size="xs" src={LightLogo} mr={2} />
                                            </Flex>
                                            <ReactMarkdown components={markdownComponents}>{msg.content}</ReactMarkdown>
                                            <Flex mt={2} align="center" direction="column">
                                                <Button
                                                    as="a"
                                                    href="https://wa.me/919747284683?text=Hello%20Dremotech%20Solutions!%20I%20would%20like%20to%20know%20more%20about%20your%20services."
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    leftIcon={<FaWhatsapp />}
                                                    colorScheme="green"
                                                    size="sm"
                                                    mb={2}
                                                >
                                                    Chat on WhatsApp
                                                </Button>
                                                <Text fontSize="sm" mb={1}>
                                                    📞{' '}
                                                    <Link href="tel:+919747284683" color={linkColor}>
                                                        +91 9747284683
                                                    </Link>
                                                </Text>
                                                <Text fontSize="sm">
                                                    📧{' '}
                                                    <Link href="mailto:dremotechsolutions@gmail.com" color={linkColor} fontSize={"10px"}>
                                                        dremotechsolutions@gmail.com
                                                    </Link>
                                                </Text>
                                            </Flex>
                                            <Text
                                                fontSize="xs"
                                                color={timestampColor}
                                                mt={1}
                                                textAlign="left"
                                            >
                                                {msg.timestamp}
                                            </Text>
                                        </Box>
                                    ) : (
                                        <Box
                                            maxW="75%"
                                            bg={msg.role === 'user' ? messageBgUser : messageBgAssistant}
                                            borderRadius="lg"
                                            p={3}
                                            color={msg.role === 'user' ? messageTextColorUser : messageTextColorAssistant}
                                            boxShadow={'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'}
                                        >
                                            {msg.role === 'assistant' && (
                                                <Flex align="center" mb={1}>
                                                    <Avatar size="xs" src={LightLogo} mr={2} />
                                                </Flex>
                                            )}
                                            <ReactMarkdown components={markdownComponents}>{msg.content}</ReactMarkdown>
                                            <Text
                                                fontSize="xs"
                                                color={timestampColor}
                                                mt={1}
                                                textAlign={msg.role === 'user' ? 'right' : 'left'}
                                            >
                                                {msg.timestamp}
                                            </Text>
                                        </Box>
                                    )}
                                </Flex>
                            ))}

                            {typingText && (
                                <Flex justify="flex-start" mb={2}>
                                    <Box
                                        maxW="75%"
                                        bg={messageBgAssistant}
                                        borderRadius="lg"
                                        p={3}
                                        color={messageTextColorAssistant}
                                    >
                                        <Text fontSize="sm" whiteSpace="pre-wrap">{typingText}</Text>
                                    </Box>
                                </Flex>
                            )}

                            {(isLoading || isTyping) && (
                                <Text fontSize="xs" color={typingColor} ml={1} mb={2}>
                                    Dremotech is typing…
                                </Text>
                            )}

                            <div ref={messagesEndRef} />
                        </Box>

                        {/* Input Area */}
                        <Flex>
                            <Input
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type your message..."
                                isDisabled={isLoading}
                                bg={inputBg}
                                color={inputTextColor}
                                _placeholder={{ color: placeholderColor }}
                                borderColor={inputBorderColor}
                                _hover={{ borderColor: inputHoverBorderColor }}
                                _focus={{ borderColor: buttonBg, boxShadow: `0 0 0 1px ${buttonBg}` }}
                            />
                            <Button
                                ml={2}
                                bg={buttonBg}
                                color="white"
                                _hover={{ bg: buttonHoverBg }}
                                onClick={handleSendMessage}
                                isLoading={isLoading}
                            >
                                Send
                            </Button>
                        </Flex>
                    </MotionBox>
                )}
            </AnimatePresence>
        </>
    );
};

export default Chatbot;